"use client";

import { useState, useEffect, useState as useStateReact } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
// FIXED: Correct shadcn/ui import
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, FileText, Send, Check, X, Pen, Download } from "lucide-react";
import { showSuccess, showError } from "@/utils/toast";
import { format } from "date-fns";
import { SignaturePad } from "@/components/SignaturePad";
import { useNavigate } from "react-router-dom";

interface Quote {
  id: string;
  quote_number: string;
  title: string;
  description: string;
  total_amount: number;
  status: 'draft' | 'sent' | 'accepted' | 'rejected';
  client_id: string | null;
  valid_until: string | null;
  created_at: string;
  clients: { name: string; email: string } | null;
  signed_at?: string;
  signature_url?: string;
  signer_id?: string;
  consent_given?: boolean;
}

const QuotesPage = () => {
  const { company, profile, getFeatureAccess } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [showSignature, setShowSignature] = useState(false);
  const [quoteToSign, setQuoteToSign] = useState<Quote | null>(null);
  const [selectedClientId, setSelectedClientId] = useStateReact<string>("");
  const [selectedStatus, setSelectedStatus] = useStateReact<string>("draft");

  // NEW: Redirect client-role users to Client Portal
  useEffect(() => {
    if (profile?.role === 'client') {
      navigate('/client-portal', { replace: true });
    }
  }, [profile, navigate]);

  // Hide quote management for client-role users
  const canManageQuotes = profile?.role !== 'client';

  // Check if this is IBP consulting tenant
  const isIBPConsulting = getFeatureAccess('ibp_consulting');

  // When opening the form (new/edit), seed the Select states from the editing item or defaults
  useEffect(() => {
    if (editingQuote) {
      setSelectedClientId(editingQuote.client_id || "");
      setSelectedStatus(editingQuote.status || "draft");
    } else {
      setSelectedClientId("");
      setSelectedStatus("draft");
    }
  }, [editingQuote, showForm]);

  const { data: quotesData, isLoading, error, refetch: refetchQuotes } = useQuery({
    queryKey: ["quotes", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data } = await supabase
        .from("quotes")
        .select("*, clients(name, email)")
        .eq("company_id", company.id)
        .order("created_at", { ascending: false });
      return data || [];
    },
  });

  const { data: clients } = useQuery({
    queryKey: ["clients", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data } = await supabase
        .from("clients")
        .select("id, name")
        .eq("company_id", company.id);
      return data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (quoteData: any) => {
      const { data, error } = await supabase
        .from("quotes")
        .insert({ ...quoteData, company_id: company?.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotes"] });
      setShowForm(false);
      showSuccess(isIBPConsulting ? "Quote created for client!" : "Quote created successfully!");
    },
    onError: () => showError(isIBPConsulting ? "Failed to create quote for client" : "Failed to create quote"),
  });

  const updateMutation = useMutation({
    mutationFn: async (quoteData: any) => {
      const { data, error } = await supabase
        .from("quotes")
        .update(quoteData)
        .eq("id", quoteData.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotes"] });
      setEditingQuote(null);
      setShowForm(false);
      showSuccess(isIBPConsulting ? "Quote updated for client!" : "Quote updated successfully!");
    },
    onError: () => showError(isIBPConsulting ? "Failed to update quote for client" : "Failed to update quote"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("quotes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotes"] });
      showSuccess(isIBPConsulting ? "Quote removed from client records" : "Quote deleted!");
    },
    onError: () => showError(isIBPConsulting ? "Failed to remove quote from client records" : "Failed to delete quote"),
  });

  const sendQuoteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data: quote } = await supabase
        .from("quotes")
        .select("*, clients(email)")
        .eq("id", id)
        .single();
      
      if (!quote) throw new Error("Quote not found");
      
      const { error } = await supabase
        .from("quotes")
        .update({ status: "sent" })
        .eq("id", id);
      
      if (error) throw error;
      
      // Send email notification
      const { error: emailError } = await supabase.functions.invoke("send-email", {
        body: {
          to: quote.clients?.email,
          subject: `Quote #${quote.quote_number} from ${company?.name}`,
          template: "quote",
          quoteId: id,
        },
      });
      
      if (emailError) throw emailError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quotes"] });
      showSuccess(isIBPConsulting ? "Quote sent to client!" : "Quote sent successfully!");
    },
    onError: () => showError(isIBPConsulting ? "Failed to send quote to client" : "Failed to send quote"),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formEl = e.currentTarget as HTMLFormElement;
    const formData = new FormData(formEl);
    const data = {
      quote_number: formData.get("quote_number") as string,
      title: formData.get("title") as string,
      description: formData.get("description") as string,
      total_amount: parseFloat(formData.get("total_amount") as string),
      status: (formData.get("status") as 'draft' | 'sent' | 'accepted' | 'rejected') || 'draft',
      client_id: (formData.get("client_id") as string) || null,
      valid_until: formData.get("valid_until") as string,
      ...(editingQuote && { id: editingQuote.id }),
    };
    if (editingQuote) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleSignQuote = (quote: Quote) => {
    setQuoteToSign(quote);
    setShowSignature(true);
  };

  const statusIcons = {
    draft: <FileText className="h-4 w-4" />,
    sent: <Send className="h-4 w-4" />,
    accepted: <Check className="h-4 w-4" />,
    rejected: <X className="h-4 w-4" />,
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">{isIBPConsulting ? "Client Quotes & Estimates" : "Quotes & Estimates"}</h1>
        {canManageQuotes && (
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                {isIBPConsulting ? "New Client Quote" : "New Quote"}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingQuote ? (isIBPConsulting ? "Edit Client Quote" : "Edit Quote") : (isIBPConsulting ? "New Client Quote" : "New Quote")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="client_id">{isIBPConsulting ? "Client" : "Customer"}</label>
                  <Select name="client_id" required>
                    <SelectTrigger>
                      <SelectValue placeholder={isIBPConsulting ? "Select Client" : "Select Customer"} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients?.map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label htmlFor="title">{isIBPConsulting ? "Quote Title" : "Title"}</label>
                  <Input id="title" name="title" defaultValue={editingQuote?.title} required />
                </div>
                <div className="space-y-2">
                  <label htmlFor="quote_number">{isIBPConsulting ? "Quote #" : "Quote Number"}</label>
                  <Input id="quote_number" name="quote_number" defaultValue={editingQuote?.quote_number || `Q-${Date.now()}`} required />
                </div>
                <div className="space-y-2">
                  <label htmlFor="total_amount">{isIBPConsulting ? "Total Amount (£)" : "Total Amount ($)"}</label>
                  <Input id="total_amount" name="total_amount" type="number" step="0.01" defaultValue={editingQuote?.total_amount} required />
                </div>
                <div className="space-y-2">
                  <label htmlFor="valid_until">Valid Until</label>
                  <Input id="valid_until" name="valid_until" type="date" defaultValue={editingQuote?.valid_until || undefined} required />
                </div>
                <div className="space-y-2">
                  <label htmlFor="notes">{isIBPConsulting ? "Client Notes" : "Notes"}</label>
                  <Textarea id="notes" name="notes" defaultValue={(editingQuote as any)?.notes} />
                </div>
                <Button type="submit" className="w-full">
                  {editingQuote ? "Update" : "Create"} {isIBPConsulting ? "Quote" : "Quote"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Signature Modal */}
      <Dialog open={showSignature} onOpenChange={setShowSignature}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Sign Quote #{quoteToSign?.quote_number}</DialogTitle>
          </DialogHeader>
          {quoteToSign && (
            <SignaturePad
              documentData={quoteToSign}
              documentType="quote"
              tableName="quotes"
              onSignComplete={() => {
                setShowSignature(false);
                refetchQuotes();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {quotesData?.map((quote: Quote) => (
          <Card key={quote.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg">{quote.title}</CardTitle>
                <div className="flex items-center space-x-1">
                  {statusIcons[quote.status as keyof typeof statusIcons]}
                  <Badge variant={quote.status === 'accepted' ? "default" : "secondary"}>
                    {quote.status}
                  </Badge>
                  {quote.consent_given && <Badge variant="outline">Signed</Badge>}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {isIBPConsulting ? "Client: " : "Customer: "} {quote.clients?.name}
              </p>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              <div className="text-lg font-bold">{isIBPConsulting ? "£" : "$"}{quote.total_amount.toFixed(2)}</div>
              {canManageQuotes && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setEditingQuote(quote); setShowForm(true); }}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {quote.status === 'draft' && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => sendQuoteMutation.mutate(quote.id)}
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Send
                    </Button>
                  )}
                  {quote.status === 'sent' && !quote.signature_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSignQuote(quote)}
                    >
                      <Pen className="h-4 w-4 mr-1" />
                      Sign Now
                    </Button>
                  )}
                  {quote.signature_url && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={quote.signature_url} target="_blank" rel="noopener noreferrer">
                        <Download className="h-4 w-4 mr-1" />
                        Download Signed
                      </a>
                    </Button>
                  )}
                  <Button variant="destructive" size="sm" onClick={() => deleteMutation.mutate(quote.id)}>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      {quotesData?.length === 0 && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">{isIBPConsulting ? "No client quotes yet. Create one to send proposals to clients!" : "No quotes yet. Create one to send proposals to customers!"}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QuotesPage;